/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import APM from '@ion/telemetry/src/apm/APM';
import {autoMonitorPagePerformance} from '@ion/telemetry/src/apm/automation/PagePerformanceAutoMonitor';
import FeatureFlags, {FeatureFlag} from '../security/FeatureFlags';

/**
 * Provides an abstraction for determining if the APM Framework is to be enabled.
 * Currently, the Framework can be fully enabled via the APM Feature Flags; i.e.
 * "?features=apm", or enabled but only to provide logging of APM metrics; that
 * is, the measurements will not be emitted to any external endpoints. This mode
 * is enabled via the APM_DEBUG Feature Flag; i.e. "?features=apmdebug".
 *
 * One the APM Framework is to be enabled by default (i.e. not hidden behind a
 * Feature Flag), this function can be updated as follows:
 *
 * ```typescript
 *   export const getAPMEnablement = () => ({
 *     enableAPMFramework : true,
 *     enableAPMDebugOnly : false
 *   });
 * ```
 */
export const getAPMEnablement = () => {
  const enableAPMFramework = FeatureFlags.isEnabled(FeatureFlag.APM);
  const enableAPMDebugOnly = FeatureFlags.isEnabled(FeatureFlag.APM_DEBUG);
  return {enableAPMFramework, enableAPMDebugOnly};
};

/**
 * Provides an abstraction for initializing the APM Framework within the application.
 *
 * One the APM Framework is to be enabled by default (i.e. not hidden behind a
 * Feature Flag), this function can be updated as follows:
 *
 * ```typescript
 *   export const initializeAPM = () => {
 *     APM.initialize(true);
 *     autoMonitorPagePerformance();
 *   };
 * ```
 *
 * @param enable When `true`, the APM Framework is to be enabled, otherwise, it is to
 *        be disabled.
 * @param debugMode  When `true`, the APM Framework is only to log results, that is,
 *        no results are to be emitted to an external endpoint.
 */
export const initializeAPM = (enable: boolean, debugMode: boolean) => {
  if (enable || debugMode) {
    APM.initialize(true, null, !debugMode);
    autoMonitorPagePerformance();
  }
};
